import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";

import useDebounceQuery from "@/hooks/useDebounceQuery";
import Input from "@/components/atoms/input/Input";
import InfiniteScrollList, { InfiniteScrollListProps } from "@/components/organisms/InfiniteScrollList";

type InfiniteScrollListSearchableProps<T> = {
  title: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  outerClassName?: string;
  noQueryResultsNode?: React.ReactNode;
} & InfiniteScrollListProps<T>;

const InfiniteScrollListSearchable = <T,>({
  title,
  onSearch,
  searchPlaceholder,
  outerClassName,
  noQueryResultsNode,
  ...listProps
}: InfiniteScrollListSearchableProps<T>) => {
  const { t } = useTranslation();
  const { query, setDebounceQuery, isTyping } = useDebounceQuery();

  useEffect(() => {
    onSearch(query);
  }, [query, onSearch]);

  return (
    <div className={outerClassName}>
      <div className="mb-4 flex items-center justify-between gap-x-10">
        <h3 className={"mb-1.5 truncate font-semibold text-primary"}>{title}</h3>
        <Input
          leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
          placeholder={searchPlaceholder || t("search.search")}
          onChange={(e) => setDebounceQuery(e.target.value)}
          className={"max-w-64"}
          sizing={"xs"}
        />
      </div>

      {query !== "" && listProps.items?.length === 0 && !listProps.isLoading ? (
        noQueryResultsNode
      ) : (
        <InfiniteScrollList {...listProps} isLoading={listProps.isLoading || isTyping} />
      )}
    </div>
  );
};

export default InfiniteScrollListSearchable;
